import React, {useEffect} from 'react';
import {blue, gray, green, red} from "@ant-design/colors";
import {
    CheckOutlined, CloseOutlined,
    DeleteOutlined, EditOutlined, FolderAddOutlined,
    FormOutlined,
    LoadingOutlined, SaveOutlined,
    SettingOutlined
} from "@ant-design/icons";
import {
    Form,
    Alert,
    Button,
    Card,
    Col,
    Collapse,
    Descriptions,
    Dropdown,
    Input,
    Modal,
    Row,
    Typography, Space, DatePicker
} from "antd";
import {ProgressViewState, SectionTypes} from "../../../../../models";
import useBackendUsers from "../../../../providers/mortgage/useBackendUsers";
import {UploadSectionBundler} from "../../application-stages/_common/uploads/Bundler";
import {Auth} from "aws-amplify";
import useMortgageUploadService from "../../../../providers/mortgage/useMortgageUploadService";
import TableOfUploads from "./TableOfUploads";
import {joinWithBr} from "../../../../../assets/scripts/string-functions";
import dayjs from "dayjs";
import UploadSectionCollapse from "./UploadSectionCollapse";


function SectionManager({items}) {

    const SectionHeader = ({section}) => {
        const labelStyle = section.isActive ? {} : {color: gray.primary};
        return <div className="d-row a-center gap-6">
            <strong style={labelStyle} className="me-auto">{section.title.toUpperCase()}</strong>
            {section.isComplete && <CheckOutlined style={{color: green.primary}}/>}
        </div>
    }

    const betterItems = items.map((section, index) => {
        const panelStyle = section.isActive ? {} : {opacity: 0.4, filter: 'grayscale(100%)'};

        return {
            key: section.name + '-' + section.index,
            label: <SectionHeader section={section}/>,
            styles: {body: {padding: 0}},
            isActive: section.isActive,
            isComplete: section.isComplete,
            children: (
                <Row style={panelStyle}>
                    <Col xs={24} xl={10} xxl={8}>
                        <TableOfUploads section={section}/>
                    </Col>
                    <Col xs={24} xl={14} xxl={16}>
                        <SectionDetailsArea section={section}/>
                    </Col>
                </Row>
            ),
        }
    })
    const disabled = betterItems.filter(it => !it.isActive)
    const enabled = betterItems.filter(it => it.isActive).sort((a, b) => {
        if (a.isComplete) return 1
        return 0
    })
    const reassembled = [...enabled, ...disabled]
    const keys = enabled.filter(it => !it.isComplete).map(it => it.key);

    return <Collapse className="mt-15" items={reassembled} defaultActiveKey={keys}/>
}


export function DraggerArea({section, children}) {
    const [over, setOver] = React.useState(false);
    const uploadService = useMortgageUploadService()
    const uploadFile = async (file) => {
        let expandedProps = {
            file,
            completeCallback: async (event) => {
                const user = await Auth.currentAuthenticatedUser()
                if (!section.progress.submittedOn) {
                    section.mortgage.mutate({
                        update: {
                            target: `progress[${section.progress.id}]`,
                            pairs: {
                                submittedOn: new Date().toISOString(),
                                submittedBy: user.attributes.sub
                            }
                        }
                    })
                }

            },
            errorCallback: (er) => {
                console.log({er})
            },
            stage: section.stage,
            section: section.name,
            index: section?.index,
        }
        await uploadService.add(expandedProps)
        // pass to the upload handler
    }

    const handleDrop = (e) => {
        e.preventDefault();
        const files = Array.from(e.dataTransfer.files);
        files.forEach(fil => {
            uploadFile(fil)
        })
        setOver(false)
    };
    const handleDragOver = (e) => {
        e.preventDefault();
        setOver(true)
    };
    const handleDragLeave = (e) => {
        e.preventDefault();
        setOver(false)
    };

    return (
        <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            style={over ? {
                border: '2px dashed #1890ff',
                borderRadius: 8,
            } : {}}
        >
            {children}
        </div>
    )
}


export function SectionDetailsArea({section}) {
    const [requestMoreOpen, setRequestMoreOpen] = React.useState(false);
    const [formModalOpen, setFormModalOpen] = React.useState(false);

    const dataProblems = section.problems.filter(it => it.type === 'DATA');
    const bundleProblems = section.problems.filter(it => it.type === 'BUNDLE');
    const userProblems = section.problems.filter(it => it.type === 'USER' || it.type === 'USER_ALERT');
    const descriptions = [
        {
            key: 1,
            label: 'Status',
            children: <div
                style={{color: section?.adminStatus?.color || gray.primary}}>{section?.adminStatus.text || 'UNKNOWN'}</div>
        },
        {
            key: 2,
            label: 'Data',
            children: dataProblems?.length ? dataProblems.map(prob => {
                return <div key={prob.key} style={{color: red.primary}}>{prob.message}</div>
            }) : <div><CheckOutlined/> OK </div>
        },
        {
            key: 3,
            label: 'Bundle',
            children: <div className="d-col gap-3"> {bundleProblems?.length ? bundleProblems.map((prob,i) => {
                return <div key={prob.key + i} className="d-row gap-3">
                    <CloseOutlined style={{color: red.primary}}/>
                    <div>{prob.message}</div>
                </div>
            }) : <div className="d-row gap-3"><CheckOutlined/> OK </div>}</div>
        },
        {
            key: 4,
            label: 'User Message',
            children: userProblems?.length ? userProblems.map((prob,i) => {
                return <div key={prob.key + i} style={{color: red.primary}}>{prob.title}</div>
            }) : <div><CheckOutlined/> OK </div>
        },
    ]
    // if (section?.props?.isStatement) {
    //     descriptions.push({
    //         key: 3,
    //         label: 'In Sequence',
    //         children: (() => {
    //             if (!section?.expandedRange?.length) {
    //                 return <div style={{color: gray.primary}}>NOT ENOUGH INFO</div>
    //             }
    //             let missing = section?.expandedRange.filter(it => it.missing) || [];
    //             if (missing.length) {
    //                 const dateStrings = missing.map(it => {
    //                     const diff = it.end.date.diff(it.start.date, 'day');
    //
    //                     return `${it.start.date.format('D MMM YY') + ' - ' + it.end.date.format('D MMM YY')} (${diff} days)`
    //                 })
    //                 return <div style={{color: red.primary}}>{dateStrings.map(s => <div
    //                     key={s}>MISSING {s}</div>)}</div>
    //             }
    //             return <div><CheckOutlined style={{color: green.primary}}/></div>
    //         })()
    //     });
    // }

    return (
        <div className="d-col">
            <Card styles={{body: {padding: 9}}}>
                <div className="d-row f-wrap">
                    <Typography.Title level={5}
                                      className="m-0">{section.personalTitle} {section.props.custom && ('(custom)')}</Typography.Title>
                    <div className="ms-auto d-row a-start mb-6 gap-6 f-wrap j-end">
                        {section.ApplicationForm && (
                            <>
                                <Button icon={<FormOutlined/>} onClick={() => setFormModalOpen(true)}>Application
                                    Data</Button>
                                {formModalOpen && (
                                    <Modal title={"Application Data"} open={formModalOpen}
                                           onCancel={() => setFormModalOpen(false)} footer={null}>
                                        <section.ApplicationForm
                                            section={section}
                                            open={formModalOpen}
                                            onClose={() => setFormModalOpen(false)}
                                        />
                                    </Modal>
                                )}
                            </>
                        )}


                        <UploadSectionBundler section={section}/>
                        <SectionSettingsDropdown section={section} setMoreRequestOpen={setRequestMoreOpen}/>

                    </div>
                </div>
                {section.progress && (
                    <>
                        <Descriptions style={{minWidth: 250}} column={2} size="small" items={descriptions}/>

                        {section.progress?.note?.length && (

                            <Alert

                                message={<strong>User Provided Note</strong>}
                                description={<div
                                    style={{fontWeight: 'bold'}}>{joinWithBr(section.progress.note.map(n => <div
                                    key={n}>{n}</div>))}</div>}
                                type="info"
                                showIcon
                                className="mt-9"
                            />)}


                        {(section.progress?.alertBody || requestMoreOpen) && (
                            <RequestMoreAlertContent section={section}/>
                        )}
                        <div className="mt-15">
                            <InternalNote key={`NoteFor:${section.key}`} section={section}/>
                        </div>
                    </>
                )}


            </Card>

        </div>
    );
}

function RequestMoreAlertContent({section, defaultEditing = false}) {
    const {me} = useBackendUsers()
    const [editing, setEditing] = React.useState(defaultEditing);
    const [saving, setSaving] = React.useState(false);
    const handleRequestMoreSave = async (values) => {
        setSaving(true);
        try {
            await section.mortgage.mutate({
                update: {
                    target: `progress[${section.progress.id}]`,
                    pairs: {
                        alertBody: values.alertBody.split('\n'),
                        alertTitle: values.alertTitle,
                        alertDismissed: false,
                        alertAuthor: me.sub
                    }
                }
            });
            setEditing(false);
            setSaving(false);
        } catch (error) {
            console.error("Error saving request more alert:", error);
        } finally {
            setSaving(false);
        }
    }

    const Message = (<div className="d-row j-end gap-3">
            <div className="d-col me-auto">
                <strong>More Requested {section?.progress?.alertAuthor === 'system' && ' (auto-generated)'}</strong>
                <Typography.Text strong type="secondary" style={{fontSize: 11}}>
                    {section.progress?.alertDismissed ? (<><CheckOutlined style={{color: green.primary}}/> Actioned By User</>) : (<><CloseOutlined style={{color: red.primary}}/> Not Actioned</>)}
                </Typography.Text>

            </div>

            <Dropdown placement="bottomRight" menu={{
                items: [
                    {
                        key: 'remove',
                        label: 'Remove',
                        icon: <DeleteOutlined/>,
                        onClick: async () => {
                            await section.mortgage.mutate({
                                update: {
                                    target: `progress[${section.progress.id}]`,
                                    pairs: {
                                        alertBody: null,
                                        alertTitle: null,
                                        alertDismissed: null,
                                        alertAuthor: null
                                    }
                                }
                            })
                        }
                    },
                    {
                        key: 'edit',
                        label: 'Edit',
                        icon: <EditOutlined/>,
                        onClick: () => {
                            setEditing(true)
                        }
                    }
                ]
            }}>
                <Button icon={<SettingOutlined/>}>Manage</Button>


            </Dropdown>
        </div>
    )

    const Description = editing ? (
        <Form initialValues={{
            alertTitle: section?.progress?.alertTitle,
            alertBody: (section?.progress?.alertBody || []).join('\n')
        }} onFinish={handleRequestMoreSave}>
            <Form.Item name="alertTitle" className="mb-9">
                <Input/>
            </Form.Item>
            <Form.Item name="alertBody" className="mb-9">
                <Input.TextArea autoSize rows={2}/>
            </Form.Item>
            <div className="d-row j-end gap-3">
                <Button onClick={() => setEditing(false)}>
                    Cancel
                </Button>
                <Button icon={saving ? <LoadingOutlined/> : <SaveOutlined/>} htmlType="submit">
                    Save
                </Button>
            </div>

        </Form>

    ) : (<div>
        <Typography.Title level={5}>{section?.progress?.alertTitle}</Typography.Title>
        {(section?.progress?.alertBody || []).map((line, index) => (
            <Typography.Text key={index} type="secondary">{line}</Typography.Text>
        ))}
    </div>)

    const fadedProp = section?.progress?.viewState === ProgressViewState.COMPLETE ? {
        style: {
            opacity: 0.4,
            filter: 'grayscale(100%)'
        }
    } : {};

    return <Alert {...fadedProp} className="mt-15" message={Message} description={Description} type="warning" showIcon/>
}

export function SectionSettingsDropdown({section, setMoreRequestOpen}) {

    const [busy, setBusy] = React.useState(false);
    let sectionCreate = {
        target: 'progress',
        pairs: {
            mortgageID: section.mortgage.id,
            applicationStage: section.stage,
            sectionType: SectionTypes.UPLOAD,
            sectionName: section.name,
            sectionIndex: section.index
        }
    }
    const handleSectionCreate = async () => {
        setBusy(true);
        try {
            await section.mortgage.mutate({
                create: sectionCreate
            });
        } catch (error) {
            console.error("Error creating section:", error);
        } finally {
            setBusy(false);
        }
    }

    if (!section?.progress) {

        return <Button onClick={handleSectionCreate} icon={busy ? <LoadingOutlined/> : <FolderAddOutlined/>}>Create
            Section Record</Button>
    }


    const handleMenuClick = async (e) => {
        setBusy(true)

        if (e.key) {
            if (e.key === 'request-more') {
                setMoreRequestOpen(true);
                setBusy(false)
                return;
            }
            if (e.key === 'delete-custom') {
                console.log('deleting custom section', section)
                await section.mortgage.mutate({
                    delete: [{
                        target: `dynamicSections`,
                        id: section.props.custom
                    },
                        {
                            target: `progress`,
                            id: section.progress.id
                        }]
                },setBusy(false))
                return;
            }

            await section.mortgage.mutate({
                update: {
                    target: `progress[${section.progress.id}].viewState`,
                    value: e.key
                }
            },setBusy(false))
        }

    };
    const options = {
        [ProgressViewState.COMPLETE]: 'Force Section Complete',
        [ProgressViewState.AUTO]: 'Restore Default',
        [ProgressViewState.HIDDEN]: 'Disable Section'
    }
    if (section?.props?.custom) {
        options['delete-custom'] = 'Delete Custom Section';
    }

    if (section?.progress?.viewState) {
        delete (options[section.progress.viewState])
    } else {
        delete (options[ProgressViewState.AUTO])
    }

    return (
        <Dropdown
            disabled={busy}
            menu={{
                items: Object.keys(options).map((key => ({
                    key,
                    label: options[key],
                }))),
                onClick: handleMenuClick,
            }}
            placement="bottomRight"
            arrow>
            <Button icon={busy ? <LoadingOutlined/> : <SettingOutlined/>}>Settings</Button>
        </Dropdown>
    );
}


export function InternalNote({section, variant = "outlined"}) {
    const {me} = useBackendUsers()
    const [value, setValue] = React.useState(section.internalNoteAsString || '');
    const handleNoteBlur = async (e) => {
        let val = e.target.value
        let pairs = {}
        if (val === "") {
            pairs.internalNote = null
            pairs.internalNoteBy = null
            pairs.internalNoteOn = null
        } else {
            pairs.internalNote = val.split('\n')
            pairs.internalNoteBy = me.sub
            pairs.internalNoteOn = new Date().toISOString()
        }
        await section.mortgage.mutate({
            update: {
                target: `progress[${section.progress.id}]`,
                pairs
            }
        })
    }
    useEffect(() => {
        setValue(section.internalNoteAsString)
    }, [section.internalNoteAsString]);
    let defaultValue = section.internalNoteAsString

    return (

        <Input.TextArea value={value} onChange={(e) => setValue(e.target.value)} variant={variant} autoSize rows={2}
                        placeholder="Internal Note ..." onClick={e => e.stopPropagation()}
                        style={{width: '100%', color: blue[6], fontWeight: 'bold'}} onBlur={handleNoteBlur}/>
    )
}


export default SectionManager;